/**
 * useLogisticsDialog Hook
 *
 * Manages logistics dialog form state, validation, and file uploads.
 * Used by LOGISTIC users to update real waste collection values.
 *
 * Created: 2025-11-02 (Phase 5 - Admin Forms Refactoring)
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  logisticsSchema,
  type LogisticsFormData,
} from '../validation/logistics.schemas';
import type { WasteCollectionFormReportDTO } from '../api/waste-collection.api';

/**
 * Hook Options
 */
interface UseLogisticsDialogOptions {
  /**
   * Order data to initialize form with
   */
  orderData: WasteCollectionFormReportDTO | null;

  /**
   * Whether the dialog is open
   * Used to reset form when dialog opens
   */
  isOpen: boolean;

  /**
   * Callback when form is submitted
   * Receives the updated logistics data
   */
  onSave: (values: LogisticsFormData) => Promise<void>;

  /**
   * Whether the save operation is in progress
   */
  loading?: boolean;
}

/**
 * Hook Return Value
 */
interface UseLogisticsDialogReturn {
  /**
   * React Hook Form instance
   */
  form: ReturnType<typeof useForm<LogisticsFormData>>;

  /**
   * Form submission handler
   */
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;

  /**
   * Whether form is submitting
   */
  loading: boolean;

  /**
   * Live total of all real values
   * Calculated using watch()
   */
  total: number;

  /**
   * File upload handler for DEEE document
   */
  handleDeeeUpload: () => void;

  /**
   * File upload handler for DBA document
   */
  handleDbaUpload: () => void;

  /**
   * Whether DEEE document is uploading
   */
  uploadingDeee: boolean;

  /**
   * Whether DBA document is uploading
   */
  uploadingDba: boolean;

  /**
   * Remove DEEE document
   */
  removeDeeeDocument: () => void;

  /**
   * Remove DBA document
   */
  removeDbaDocument: () => void;
}

/**
 * useLogisticsDialog Hook
 *
 * Provides form state, validation, and file upload handlers for logistics dialog.
 *
 * @example
 * ```tsx
 * function LogisticsDialog({ orderData, isOpen, onSave, loading }) {
 *   const {
 *     form,
 *     onSubmit,
 *     total,
 *     handleDeeeUpload,
 *     uploadingDeee,
 *   } = useLogisticsDialog({
 *     orderData,
 *     isOpen,
 *     onSave,
 *     loading,
 *   });
 *
 *   return (
 *     <Form {...form}>
 *       <form onSubmit={onSubmit}>
 *         <FormField name="realDeeeKg" />
 *         <p>Total: {total} kg</p>
 *         <Button onClick={handleDeeeUpload}>Upload DEEE</Button>
 *       </form>
 *     </Form>
 *   );
 * }
 * ```
 */
export function useLogisticsDialog({
  orderData,
  isOpen,
  onSave,
  loading = false,
}: UseLogisticsDialogOptions): UseLogisticsDialogReturn {
  const [uploadingDeee, setUploadingDeee] = useState(false);
  const [uploadingDba, setUploadingDba] = useState(false);

  // Initialize form with Zod validation
  const form = useForm<LogisticsFormData>({
    resolver: zodResolver(logisticsSchema),
    defaultValues: {
      realDeeeKg: 0,
      realDbaKg: 0,
      realBecuriSiNeoaneKg: 0,
      deeeOrderDocument: '',
      dbaOrderDocument: '',
    },
    mode: 'onBlur',
  });

  // Watch all real values for live total calculation
  const realDeeeKg = form.watch('realDeeeKg') || 0;
  const realDbaKg = form.watch('realDbaKg') || 0;
  const realBecuriSiNeoaneKg = form.watch('realBecuriSiNeoaneKg') || 0;

  // Calculate live total
  const total = realDeeeKg + realDbaKg + realBecuriSiNeoaneKg;

  /**
   * Initialize form with order data when dialog opens
   */
  useEffect(() => {
    if (orderData && isOpen) {
      form.reset({
        realDeeeKg: orderData.realDeeeKg || orderData.deeeKg || 0,
        realDbaKg: orderData.realDbaKg || orderData.dbaKg || 0,
        realBecuriSiNeoaneKg:
          orderData.realBecuriSiNeoaneKg || orderData.becuriSiNeoaneKg || 0,
        deeeOrderDocument: orderData.deeeOrderDocument || '',
        dbaOrderDocument: orderData.dbaOrderDocument || '',
      });
    }
  }, [orderData, isOpen, form]);

  /**
   * Handle form submission
   */
  const handleSubmit = async (data: LogisticsFormData) => {
    try {
      await onSave(data);
    } catch (error) {
      console.error('Error saving logistics data:', error);
      // Error handling done by parent component (toasts, etc.)
    }
  };

  /**
   * Generic file upload handler
   * Uses FileReader to convert file to data URL
   * In production, this should upload to Azure Blob Storage
   */
  const handleFileUpload = async (
    field: 'deeeOrderDocument' | 'dbaOrderDocument'
  ) => {
    // Create hidden file input
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';

    const setUploading =
      field === 'deeeOrderDocument' ? setUploadingDeee : setUploadingDba;

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      setUploading(true);

      try {
        // TODO: Replace with actual Azure Blob Storage upload
        // const url = await filesApi.uploadFile(file, FILE_FOLDERS.LOGISTICS);
        // form.setValue(field, url);

        // Temporary: Create data URL for preview
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          form.setValue(field, result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error(`Error uploading ${field}:`, error);
      } finally {
        setUploading(false);
      }
    };

    input.click();
  };

  /**
   * Handle DEEE document upload
   */
  const handleDeeeUpload = () => {
    handleFileUpload('deeeOrderDocument');
  };

  /**
   * Handle DBA document upload
   */
  const handleDbaUpload = () => {
    handleFileUpload('dbaOrderDocument');
  };

  /**
   * Remove DEEE document
   */
  const removeDeeeDocument = () => {
    form.setValue('deeeOrderDocument', '');
  };

  /**
   * Remove DBA document
   */
  const removeDbaDocument = () => {
    form.setValue('dbaOrderDocument', '');
  };

  return {
    form,
    onSubmit: form.handleSubmit(handleSubmit),
    loading,
    total,
    handleDeeeUpload,
    handleDbaUpload,
    uploadingDeee,
    uploadingDba,
    removeDeeeDocument,
    removeDbaDocument,
  };
}
